import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Search, RefreshCw, CheckCircle2, Trash2, Pencil, Phone, RotateCcw } from 'lucide-react';
import { complainsApi, usersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';

type Status = 'Open' | 'Completed' | 'Cancelled';

const EMPTY_NEW = { phone_no: '', remark: '' };
const EMPTY_CLOSE = { customer_name: '', contact_person: '', closing_remark: '', status: 'Completed' as 'Completed' | 'Cancelled' };

const statusClass = (s: string) =>
  s === 'Completed' ? 'bg-green-50 text-green-700 border-green-200'
  : s === 'Cancelled' ? 'bg-gray-100 text-gray-600 border-gray-200'
  : 'bg-amber-50 text-amber-700 border-amber-200';

/** "2h 15m" / "3d 4h" — how long a complain took to resolve. */
const fmtDuration = (mins: number | null) => {
  if (mins === null || mins === undefined) return '—';
  if (mins < 1) return '<1m';
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const fmtStamp = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v).slice(0, 16).replace('T', ' ');
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Complains: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { canCreate, canEdit, canDelete } = useAuth();

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'Open' | 'Completed' | 'Cancelled' | 'all'>('Open');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [staff, setStaff] = useState('all');
  const [staffList, setStaffList] = useState<{ name: string }[]>([]);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_NEW });
  const [saving, setSaving] = useState(false);

  const [editFor, setEditFor] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_NEW });

  const [closeFor, setCloseFor] = useState<any | null>(null);
  const [closeForm, setCloseForm] = useState({ ...EMPTY_CLOSE });

  const [deleteFor, setDeleteFor] = useState<any | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await complainsApi.getAll({ status, search: debounced, staff, limit: 200 });
      if (res.success) { setRows(res.data || []); setTotal(res.total || 0); }
      else showError('Error', res.message || 'Failed to load complains');
    } catch (e: any) {
      showError('Error', e.message || 'Failed to load complains');
    } finally {
      setLoading(false);
    }
  }, [status, debounced, staff, showError]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    usersApi.getBasic().then(r => { if (r.success) setStaffList(r.data || []); }).catch(() => {});
  }, []);

  const submitNew = async () => {
    if (form.phone_no.replace(/\D/g, '').length < 10) { showError('Required', 'Enter a valid 10-digit contact number'); return; }
    if (!form.remark.trim()) { showError('Required', 'Enter a remark'); return; }
    setSaving(true);
    try {
      const res = await complainsApi.create({ phone_no: form.phone_no, remark: form.remark.trim() });
      if (res.success) {
        showSuccess('Logged', 'Complain logged');
        setForm({ ...EMPTY_NEW });
        setShowAdd(false);
        load();
      } else showError('Error', res.message || 'Failed to log');
    } catch (e: any) { showError('Error', e.message || 'Failed to log'); }
    finally { setSaving(false); }
  };

  const submitEdit = async () => {
    if (!editFor) return;
    setSaving(true);
    try {
      const res = await complainsApi.update(editFor.id, { phone_no: editForm.phone_no, remark: editForm.remark });
      if (res.success) { showSuccess('Saved', 'Complain updated'); setEditFor(null); load(); }
      else showError('Error', res.message || 'Failed to update');
    } catch (e: any) { showError('Error', e.message || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const submitClose = async () => {
    if (!closeFor) return;
    setSaving(true);
    try {
      const res = await complainsApi.close(closeFor.id, closeForm);
      if (res.success) {
        showSuccess('Closed', res.message || 'Complain closed');
        setCloseFor(null);
        setCloseForm({ ...EMPTY_CLOSE });
        load();
      } else showError('Error', res.message || 'Failed to close');
    } catch (e: any) { showError('Error', e.message || 'Failed to close'); }
    finally { setSaving(false); }
  };

  const doReopen = async (r: any) => {
    setBusyId(r.id);
    try {
      const res = await complainsApi.reopen(r.id);
      if (res.success) { showSuccess('Reopened', 'Back in the open queue'); load(); }
      else showError('Error', res.message || 'Failed');
    } catch (e: any) { showError('Error', e.message || 'Failed'); }
    finally { setBusyId(null); }
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    setBusyId(deleteFor.id);
    try {
      const res = await complainsApi.remove(deleteFor.id);
      if (res.success) { showSuccess('Deleted', 'Complain removed'); setDeleteFor(null); load(); }
      else showError('Error', res.message || 'Failed');
    } catch (e: any) { showError('Error', e.message || 'Failed'); }
    finally { setBusyId(null); }
  };

  const th = 'py-2.5 px-3 text-left border border-gray-200';

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 w-full">

        {/* Header — title, filters and Add on one line */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h1 className="text-lg font-semibold text-gray-800 shrink-0">Complain Report</h1>

          <div className="flex gap-1">
            {(['Open', 'Completed', 'Cancelled', 'all'] as const).map(s => (
              <button key={s} onClick={() => setStatus(s)}
                className={`text-xs font-medium px-3 py-1.5 rounded border transition-colors ${
                  status === s ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>

          <select value={staff} onChange={e => setStaff(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5 text-gray-700">
            <option value="all">All Staff</option>
            {staffList.map(u => <option key={u.name} value={u.name}>{u.name}</option>)}
          </select>

          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search phone / remark / customer…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400" />
          </div>

          <button onClick={load} title="Refresh"
            className="shrink-0 h-9 w-9 inline-flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>

          {canCreate('tasks') && (
            <button onClick={() => setShowAdd(true)}
              className="ml-auto shrink-0 flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded px-4 py-2">
              <Plus size={16} /> Add Complain
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No complains found.</div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wide">
                  <th className={`${th} w-10`}>#</th>
                  <th className={`${th} w-32`}>Contact No</th>
                  <th className={th}>Complain</th>
                  <th className={`${th} w-44`}>Customer</th>
                  <th className={`${th} w-36`}>Contact Person</th>
                  <th className={`${th} w-40`}>Created By</th>
                  <th className={`${th} w-40`}>Closed By</th>
                  <th className={`${th} w-24`}>Resolve Time</th>
                  <th className={`${th} w-28`}>Status</th>
                  <th className={`${th} w-28 text-center`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.id} className="hover:bg-blue-50/40">
                    <td className="py-2 px-3 text-gray-500 tabular-nums border border-gray-200">{idx + 1}</td>
                    <td className="py-2 px-3 border border-gray-200 whitespace-nowrap">
                      <a href={`tel:${r.phone_no}`} className="text-blue-600 hover:underline flex items-center gap-1">
                        <Phone size={12} /> {r.phone_no}
                      </a>
                    </td>
                    <td className="py-2 px-3 text-gray-900 border border-gray-200">
                      <div className="truncate max-w-[320px]" title={r.remark}>{r.remark}</div>
                      {r.closing_remark && (
                        <div className="text-xs text-gray-500 truncate max-w-[320px]" title={r.closing_remark}>
                          Closing: {r.closing_remark}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-gray-800 border border-gray-200">
                      {r.customer_name || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 px-3 text-gray-800 border border-gray-200">
                      {r.contact_person || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 px-3 text-gray-700 border border-gray-200 whitespace-nowrap">
                      <div>{r.created_by}</div>
                      <div className="text-xs text-gray-500 tabular-nums">{fmtStamp(r.created_at)}</div>
                    </td>
                    <td className="py-2 px-3 text-gray-700 border border-gray-200 whitespace-nowrap">
                      {r.closed_by ? (
                        <>
                          <div>{r.closed_by}</div>
                          <div className="text-xs text-gray-500 tabular-nums">{fmtStamp(r.closed_at)}</div>
                        </>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 px-3 border border-gray-200 tabular-nums whitespace-nowrap font-medium text-gray-800">
                      {fmtDuration(r.resolve_minutes)}
                    </td>
                    <td className="py-2 px-3 border border-gray-200">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusClass(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 border border-gray-200">
                      <div className="flex items-center justify-center gap-1">
                        {r.status === 'Open' && canEdit('tasks') && (
                          <>
                            <button onClick={() => { setCloseFor(r); setCloseForm({ ...EMPTY_CLOSE }); }}
                              title="Close complain" className="text-green-600 hover:text-green-800 p-1">
                              <CheckCircle2 size={16} />
                            </button>
                            <button onClick={() => { setEditFor(r); setEditForm({ phone_no: r.phone_no, remark: r.remark }); }}
                              title="Edit" className="text-blue-600 hover:text-blue-800 p-1">
                              <Pencil size={15} />
                            </button>
                          </>
                        )}
                        {r.status !== 'Open' && canEdit('tasks') && (
                          <button onClick={() => doReopen(r)} disabled={busyId === r.id}
                            title="Reopen" className="text-amber-600 hover:text-amber-800 p-1 disabled:opacity-50">
                            <RotateCcw size={15} />
                          </button>
                        )}
                        {canDelete('tasks') && (
                          <button onClick={() => setDeleteFor(r)} title="Delete" className="text-red-500 hover:text-red-700 p-1">
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="text-xs text-gray-500 uppercase tracking-wide mt-3">Total {total} complain{total === 1 ? '' : 's'}</div>
        )}
      </div>

      {/* Add complain — only a contact number and what the issue is */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => { setShowAdd(false); setForm({ ...EMPTY_NEW }); }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-800">Add Complain</h3>
              <button onClick={() => { setShowAdd(false); setForm({ ...EMPTY_NEW }); }}
                className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Contact No <span className="text-red-500">*</span>
                </label>
                <input autoFocus type="tel" inputMode="numeric" value={form.phone_no}
                  onChange={e => setForm({ ...form, phone_no: e.target.value })}
                  placeholder="10-digit mobile number"
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Complain <span className="text-red-500">*</span>
                </label>
                <textarea rows={3} value={form.remark}
                  onChange={e => setForm({ ...form, remark: e.target.value })}
                  placeholder="What is the issue?"
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <p className="text-xs text-gray-500">
                Customer name and contact person are filled in when the complain is closed.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => { setShowAdd(false); setForm({ ...EMPTY_NEW }); }}
                className="text-sm px-4 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submitNew} disabled={saving || !form.phone_no.trim() || !form.remark.trim()}
                className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit an open complain */}
      {editFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditFor(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-800">Edit Complain</h3>
              <button onClick={() => setEditFor(null)} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Contact No</label>
                <input type="tel" inputMode="numeric" value={editForm.phone_no}
                  onChange={e => setEditForm({ ...editForm, phone_no: e.target.value })}
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Complain</label>
                <textarea rows={3} value={editForm.remark}
                  onChange={e => setEditForm({ ...editForm, remark: e.target.value })}
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => setEditFor(null)}
                className="text-sm px-4 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submitEdit} disabled={saving}
                className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close — this is where the customer details get filled in */}
      {closeFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCloseFor(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-800">Close Complain</h3>
                <button onClick={() => setCloseFor(null)} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{closeFor.phone_no} · {closeFor.remark}</p>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Customer Name</label>
                <input autoFocus value={closeForm.customer_name}
                  onChange={e => setCloseForm({ ...closeForm, customer_name: e.target.value })}
                  placeholder="Who was it?"
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Contact Person Name</label>
                <input value={closeForm.contact_person}
                  onChange={e => setCloseForm({ ...closeForm, contact_person: e.target.value })}
                  placeholder="Who did you speak to?"
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Outcome</label>
                <div className="flex gap-2">
                  {(['Completed', 'Cancelled'] as const).map(s => (
                    <button key={s} onClick={() => setCloseForm({ ...closeForm, status: s })}
                      className={`flex-1 text-sm font-medium px-3 py-2 rounded border transition-colors ${
                        closeForm.status === s
                          ? (s === 'Completed' ? 'bg-green-50 text-green-700 border-green-300' : 'bg-gray-100 text-gray-700 border-gray-300')
                          : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                      }`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Closing Remark</label>
                <textarea rows={3} value={closeForm.closing_remark}
                  onChange={e => setCloseForm({ ...closeForm, closing_remark: e.target.value })}
                  placeholder="How was it resolved?"
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={() => setCloseFor(null)}
                className="text-sm px-4 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submitClose} disabled={saving}
                className="text-sm px-4 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Saving…' : `Mark ${closeForm.status}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteFor(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Delete complain?</h3>
            <p className="text-xs text-gray-500 mb-4">
              {deleteFor.phone_no} — “{deleteFor.remark}”. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteFor(null)}
                className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={doDelete} disabled={busyId === deleteFor.id}
                className="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Complains;
